import { ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from 'recharts'
import { CHART_COLORS, CHART_PALETTE } from '@/lib/chart-theme'
import { StatCard, fmt$, InfoTooltip } from './shared'

export function MarkupAnalysisReport({ data }: { data: any }) {
  const { summary, chartData } = data
  const { byEstimator, companyAvg } = chartData

  // Build scatter data: x = markup %, y = total value, z = estimate count (bubble size)
  const scatterData = byEstimator.map((est: any, idx: number) => ({
    x: est.avgMarkup,
    y: est.totalValue,
    z: est.estimateCount * 100, // scale up for visibility
    name: est.estimatorName,
    estimatorId: est.estimatorId,
    estimateCount: est.estimateCount,
    totalValue: est.totalValue,
    avgMarkup: est.avgMarkup,
    color: CHART_PALETTE[idx % CHART_PALETTE.length],
  }))

  // Custom tooltip for scatter chart
  const CustomScatterTooltip = ({ active, payload }: any) => {
    if (active && payload && payload[0]) {
      const data = payload[0].payload
      return (
        <div style={{
          backgroundColor: '#1E293B',
          border: '1px solid #334155',
          borderRadius: '6px',
          padding: '8px 12px',
          color: '#F1F5F9',
          fontSize: '12px',
        }}>
          <p style={{ margin: '0 0 4px 0', fontWeight: 500 }}>{data.name}</p>
          <p style={{ margin: '2px 0', color: '#CBD5E1' }}>Markup: {data.avgMarkup}%</p>
          <p style={{ margin: '2px 0', color: '#CBD5E1' }}>Total Value: {fmt$(data.totalValue)}</p>
          <p style={{ margin: '2px 0', color: '#CBD5E1' }}>Estimates: {data.estimateCount}</p>
        </div>
      )
    }
    return null
  }

  return (
    <div className="rep-report">
      <div className="rep-stats-row">
        <StatCard title="Company Avg Markup" value={`${summary.companyAvgMarkup}%`} color={CHART_COLORS.purple} />
        <StatCard title="Estimators" value={summary.estimatorCount} />
      </div>

      <div className="rep-charts-row">
        <div className="rep-chart-card rep-chart-full">
          <InfoTooltip text="A scatter plot reveals the relationship between markup percentage and total estimate value — bubble size shows estimate count. Estimators above the company average line command higher markups." />
          <ResponsiveContainer width="100%" height={380}>
            <ScatterChart margin={{ top: 10, right: 20, bottom: 50, left: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis
                type="number"
                dataKey="x"
                name="Avg Markup %"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#94A3B8' }}
                label={{ value: 'Average Markup %', position: 'insideBottomRight', offset: -15, fill: '#64748B', fontSize: 12 }}
              />
              <YAxis
                type="number"
                dataKey="y"
                name="Total Value"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#94A3B8' }}
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                label={{ value: 'Total Estimate Value', angle: -90, position: 'insideLeft', fill: '#64748B', fontSize: 12 }}
              />
              <ZAxis type="number" dataKey="z" range={[50, 400]} name="Estimate Count" />
              <Tooltip content={<CustomScatterTooltip />} cursor={{ strokeDasharray: '3 3' }} />
              <ReferenceLine
                x={companyAvg}
                stroke={CHART_COLORS.coral}
                strokeDasharray="6 4"
                label={{
                  value: `Co. Avg ${companyAvg}%`,
                  position: 'top',
                  fill: '#94A3B8',
                  fontSize: 11,
                  offset: 10,
                }}
              />
              <Scatter
                name="Estimators"
                data={scatterData}
              >
                {scatterData.map((entry: any, idx: number) => (
                  <Cell key={`cell-${idx}`} fill={entry.color} />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rep-table-card">
        <h3 className="rep-chart-title">Markup Details</h3>
        <table className="rep-table">
          <thead><tr><th>Estimator</th><th>Avg Markup</th><th>Estimates</th><th>Total Value</th></tr></thead>
          <tbody>
            {byEstimator.map((r: any) => (
              <tr key={r.estimatorId}>
                <td>{r.estimatorName}</td><td>{r.avgMarkup}%</td><td>{r.estimateCount}</td>
                <td>{fmt$(r.totalValue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
